import React, { useCallback, useContext, useMemo } from 'react';
import './styles.scss';
import { Modify } from 'utils/types';
import {
  CurrentStatus,
  Reservation,
  Table as TableType,
} from 'types/reservations';
import Table, { bar1Box, labelUnderTable, visibleTimes } from '../NewTable';
import {
  blockToTime,
  classHelper,
  findOverlaps,
  timeToBlock,
} from 'utils/helper';
import {
  shiftBlockableForTable,
  tableBlockCoversWindow,
  tableBlockPillLabel,
} from 'utils/tableBlocks';
import { ReservationContext } from 'Contexts/ReservationContext';
import useRestaurant from 'CustomHooks/useRestaurant';
import { useTableInfo } from 'CustomHooks/useTableInfo';
import { hasRecentDragGesture } from 'CustomHooks/useDragAndDrop';
import {
  getSharedTableCapacityInfo,
  SharedTablePool,
} from 'utils/sharedTableCapacity';
import {
  getLastBlockOfShift,
  getRunningShiftId,
  getShiftOfReservation,
  isReservationVisibleInShift,
} from 'utils/reservationShift';

const sizeHelper = (size: number) => {
  if (size < 80) {
    return 'xsmall';
  } else if (size < 110) {
    return 'small';
  } else if (size < 130) {
    return 'medium';
  }
  return '';
};

const TABLE_CLICK_DRAG_THRESHOLD_PX = 8;
const TABLE_TOUCH_CLICK_DRAG_THRESHOLD_PX = 16;
const TABLE_CLICK_SUPPRESS_MS = 500;

/**
 * Pixel rect of a table on the floor plan. Shared with FloorPlan's group-color
 * wash layer so the washes stay aligned with the tables they belong to.
 */
export const computeTablePosition = (
  table: Pick<TableType, 'type' | 'coords'>,
  size: number,
): {
  top: number;
  left: number;
  width: number;
  height: number;
  borderRadius?: string;
} => {
  if (table.type === 'Bar1') {
    const SIZE = table.coords.size * size;
    const box = bar1Box(table.coords.direction);
    const width = SIZE * box.width;
    const height = SIZE * box.height;

    return {
      top: table.coords.y * size + (SIZE - height) / 2,
      left: table.coords.x * size + (SIZE - width) / 2,
      width,
      height,
    };
  }

  if (
    table.type === '8Long' ||
    table.type === '8L' ||
    table.type === '10Long'
  ) {
    let direction = Math.abs(table.coords.direction % 180);

    let SIZE = table.coords.size * size;

    if (direction < 20 || direction > 170) {
      return {
        top: table.coords.y * size + SIZE * 0.3,
        left: table.coords.x * size,
        width: SIZE,
        height: SIZE * 0.6,
      };
    } else if (direction > 70 && direction < 110) {
      return {
        top: table.coords.y * size,
        left: table.coords.x * size + SIZE * 0.3,
        width: SIZE * 0.6,
        height: SIZE,
      };
    } else {
      return {
        top: table.coords.y * size,
        left: table.coords.x * size,
        width: SIZE,
        height: SIZE,
      };
    }
  }

  return {
    top: table.coords.y * size,
    left: table.coords.x * size,
    width: table.coords.size * size,
    height: table.coords.size * size,
    borderRadius: '50%',
  };
};

export type TablePointerStart = {
  x: number;
  y: number;
  pointerId: number;
  pointerType: string;
};

export type TableWrapperProps = {
  table: Modify<TableType, { reservations: Reservation[] }>;
  touchingX?: string[];
  touchingY?: string[];
  editMode: null | {
    start: number | null;
    end: number | null;
    id: string;
    guests?: number;
    occassion?: string;
    reservation?: Reservation;
  };
  isSelected: boolean;
  onTableClick: (
    id: string,
    name: string,
    space: string,
    available?: boolean,
    switchReservation?: string | 'blocked',
  ) => void;
  onTableDragStart?: (
    id: string,
    name: string,
    space: string,
    available?: boolean,
    switchReservation?: string | 'blocked',
    pointerStart?: TablePointerStart,
  ) => void;
  size: number;
  active: boolean;
  hasAlert?: boolean;
  currentTime: string | null;
  switchReservation?: string | 'blocked';
  /**
   * Keep the table mounted but visually removed (`display:none`) and
   * non-interactive. Used to hold inactive spaces' tables in the DOM during a
   * reservation drag so the TouchBackend drag-source node survives a mid-drag
   * space switch instead of being unmounted.
   */
  hidden?: boolean;
  /** Day-wide pool data, computed once in FloorPlan and passed down. */
  sharedTablePool?: SharedTablePool | null;
};

const TableWrapper: React.FC<TableWrapperProps> = ({
  table,
  editMode,
  isSelected,
  onTableClick,
  onTableDragStart,
  size,
  active,
  currentTime,
  hasAlert,
  switchReservation,
  hidden = false,
  touchingX = [],
  touchingY = [],
  sharedTablePool = null,
}) => {
  const position = useMemo(() => computeTablePosition(table, size), [table, size]);

  const { showTimesInEditMode, shift, shiftWindow, shifts, hideLiveResas } =
    useContext(ReservationContext);

  const { experimentalFlags } = useRestaurant();

  const tableInfo = useTableInfo(table.name);

  // const { reservationSettings } = useRestaurant();

  const onlyReservationsOfActiveShift = true;
  const strictShiftStartFilter =
    !!experimentalFlags?.data?.strictShiftStartFilter;

  // With this flag the shift usually comes from the clock, so an edit with a
  // time shows the shift of that time instead of the selected one.
  const shiftFollowsClock =
    !!experimentalFlags?.data?.onlyShowReservationsThatStartInCurrentShift;

  const editShiftWindow = useMemo(() => {
    if (
      !shiftFollowsClock ||
      !shiftWindow ||
      editMode?.start == null ||
      !shifts?.length
    )
      return null;

    // When no shift takes the occasion at this time, the book's rule for the
    // running shift decides, and without one the selected shift stays.
    const id =
      getShiftOfReservation(
        {
          occassion: editMode.occassion || '',
          startTimeInBlocks: editMode.start,
        },
        shifts,
        true,
      ) || getRunningShiftId(shifts, editMode.start);
    const editShift = shifts.find((s) => s.id === id);

    return editShift
      ? {
          start: editShift.start,
          close: getLastBlockOfShift(editShift, shifts),
        }
      : null;
  }, [
    shiftFollowsClock,
    shiftWindow,
    editMode?.start,
    editMode?.occassion,
    shifts,
  ]);

  // Not filtered by shift: a drop also collides with what the shift hides.
  const dropTimes = useMemo(() => {
    return table.reservations
      .filter(
        (r) =>
          !r.done &&
          !r.blocked &&
          r.status !== 'failed' &&
          editMode?.id !== r.id &&
          (!editMode || showTimesInEditMode) &&
          // Shared tables always show every concurrent reservation during edit
          // mode so the user can see the capacity picture at a glance.
          (table.shared ||
            !!experimentalFlags?.data?.showAllTimesInEditMode ||
            !editMode?.start ||
            Math.abs(editMode.start - timeToBlock(r.time)) <
              (editMode.reservation?.reservationLength || 6)),
      )
      .map((r) => {
        let isNew = !!r.createdAt && r.createdAt > Date.now() - 30 * 60000;

        let time = r.time;
        let color = isNew ? 'var(--color-tertiary)' : 'var(--color-primary)';
        let textColor = isNew ? 'var(--color-white)' : 'var(--color-onPrimary)';
        let guests = r.guests;

        if (editMode) {
          let before = timeToBlock(r.time) > (editMode?.start || 0);

          time = !before
            ? `bis ${blockToTime(timeToBlock(r.time) + r.reservationLength)}`
            : `ab ${r.time}`;

          color = 'var(--color-tertiary)';
          textColor = '#ffffff';
        }

        // `time` may carry an "ab …"/"bis …" display label in edit mode, so
        // the block math has to use the reservation's raw time.
        let from = r.startTimeInBlocks ?? timeToBlock(r.time);

        return {
          id: r.id,
          color,
          textColor,
          time,
          guests,
          status: editMode ? CurrentStatus.NORMAL : r.currentStatus,
          customStatus: r.customData?.customStatus as string,
          from,
          till: from + r.reservationLength,
          fixed: !!r.fixed,
          partlySeated: !!r.partlySeated,
          tables: r.tables,
          guestName: r.guest?.name || '',
          changed: !!(r as any).tablePlanAssistantChanged || !!r.previousTables,
        };
      });
  }, [table, editMode, showTimesInEditMode, experimentalFlags]);

  const times = useMemo(() => {
    const shownWindow = editShiftWindow ?? shiftWindow;

    if (!onlyReservationsOfActiveShift || !shownWindow) {
      return dropTimes;
    }

    const shown = table.reservations
      .filter(
        (r) =>
          isReservationVisibleInShift(
            r,
            shownWindow,
            hideLiveResas,
            currentTime,
            strictShiftStartFilter,
          ) ||
          // The booking may run into another shift and collide there.
          (shiftFollowsClock &&
            editMode?.start != null &&
            editMode?.end != null &&
            findOverlaps(r, editMode.start, editMode.end)),
      )
      .map((r) => r.id);

    return dropTimes.filter((t) => shown.includes(t.id));
  }, [
    dropTimes,
    table,
    editMode,
    shiftWindow,
    editShiftWindow,
    hideLiveResas,
    shiftFollowsClock,
    strictShiftStartFilter,
    currentTime,
  ]);

  // A block scoped to one shift must not mark the table in another shift.
  const activeBlocks = useMemo(
    () =>
      table.reservations.filter(
        (r) =>
          r.blocked &&
          (!shift || tableBlockCoversWindow(r, shift.start, shift.close)),
      ),
    [table, shift],
  );

  const blocked = activeBlocks.length > 0;

  const spaceClosedLabel = useMemo(() => {
    if (!experimentalFlags?.data?.blockPerShift) return null;

    const relevant = (shifts ?? []).filter(
      (s) =>
        s.active &&
        (!shift || s.id === shift.id) &&
        shiftBlockableForTable(s as any, table),
    );
    if (!relevant.length) return null;

    const closing = relevant.filter((s) =>
      s.overwrite?.closedSpaces?.includes(table.space),
    );
    if (!closing.length) return null;

    return closing.length === relevant.length ? 'Bereich zu' : 'Bereich teils zu';
  }, [shifts, shift, table, experimentalFlags]);

  // Lock marker under the table: only for blocked tables, and only with the
  // experimental flag on.
  const blockPillLabel = useMemo(
    () =>
      experimentalFlags?.data?.blockPerShift
        ? tableBlockPillLabel(activeBlocks)
        : null,
    [activeBlocks, experimentalFlags],
  );

  // For shared tables in edit mode: compute how many seats at THIS
  // reservation's time window are already taken. Surfaced on NewTable as
  // a capacity pill under the table name.
  const sharedEditInfo = useMemo(() => {
    if (!table.shared) return null;
    if (!editMode || editMode.start === null || editMode.start === undefined)
      return null;
    if (editMode.end === null || editMode.end === undefined) return null;

    return getSharedTableCapacityInfo(table.reservations, {
      start: editMode.start,
      end: editMode.end,
      requestedGuests: editMode.guests,
      tableMax: table.max,
      tableName: table.name,
      id: editMode.id,
      pool: sharedTablePool,
    });
  }, [table, editMode, sharedTablePool]);

  const available = useMemo(() => {
    if (!editMode || editMode?.start === null) return false;
    if (blocked) return false;

    // For non-shared tables, any time overlap blocks — existing behavior.
    // For shared tables in edit mode, a click selects a table into a larger
    // table pool. The table is selectable while it still has seats to
    // contribute; the full party size is validated against the selected pool.
    if (!table.shared) {
      return table.reservations.every((r) => {
        let overlaps = findOverlaps(r, editMode.start || 0, editMode.end || 0);
        let sameReservation = r.id === editMode.id;
        return !overlaps || sameReservation;
      });
    }

    if (sharedEditInfo) return sharedEditInfo.remaining > 0;
    return true;
  }, [table, editMode, sharedEditInfo, blocked]);

  const timeline = useMemo(() => {
    if (currentTime === null || !!editMode || hideLiveResas) return null;

    let activeRes = table.reservations.find(
      (r) =>
        typeof r.started === 'number' && !r.done &&
        (!strictShiftStartFilter || times.some((t) => t.id === r.id)),
    );

    if (activeRes) {
      let total =
        (activeRes?.endTimeInBlocks || 0) - (activeRes?.startTimeInBlocks || 0);

      let current = Math.min(
        total,
        timeToBlock(currentTime) - (activeRes?.startTimeInBlocks || 0),
      );

      return {
        current,
        total,
      };
    } else {
      return null;
    }
  }, [table, currentTime, times, editMode, hideLiveResas, strictShiftStartFilter]);

  const status = useMemo(() => {
    let status: CurrentStatus | 'nonbookable' = CurrentStatus.NORMAL;

    if (blocked) {
      status = 'nonbookable';
    } else if (times.length) {
      status = times[0].status || CurrentStatus.NORMAL;
    } else if (!table.occassions.length) {
      status = 'nonbookable';
    }

    if (status === 'nonbookable' || status === CurrentStatus.NORMAL) {
      if (tableInfo.some((x) => x.open && x.balance)) {
        status = CurrentStatus.HAS_ORDERED;
      }
    }

    return status;
  }, [times, table, blocked, tableInfo]);

  const quandooTableView = !!experimentalFlags?.data?.quandooTableView;
  const shownTimes = visibleTimes(
    times,
    !!experimentalFlags?.data?.hideTimeOfCurrentReservation,
  );
  // A reservation carried over from an earlier shift is not the next one.
  const nextResa =
    quandooTableView && !editMode && status !== CurrentStatus.SEATED
      ? shownTimes.find((t) => !shift || t.from >= shift.start)
      : null;

  const pointerStartRef = React.useRef<{
    x: number;
    y: number;
    pointerId: number;
    pointerType: string;
  } | null>(null);
  const suppressNextClickRef = React.useRef(false);

  const markDragLikeGesture = useCallback(() => {
    suppressNextClickRef.current = true;
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      suppressNextClickRef.current ||
      hasRecentDragGesture(TABLE_CLICK_SUPPRESS_MS)
    ) {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
      return;
    }

    suppressNextClickRef.current = false;
    onTableClick(
      table.id,
      table.name,
      table.space,
      available,
      switchReservation,
    );
  };

  const handlePointerDown = onTableDragStart
    ? (event: React.PointerEvent<HTMLDivElement>) =>
        onTableDragStart(
          table.id,
          table.name,
          table.space,
          available,
          switchReservation,
          {
            x: event.clientX,
            y: event.clientY,
            pointerId: event.pointerId,
            pointerType: event.pointerType,
          },
        )
    : (event: React.PointerEvent<HTMLDivElement>) => {
        pointerStartRef.current = {
          x: event.clientX,
          y: event.clientY,
          pointerId: event.pointerId,
          pointerType: event.pointerType,
        };
        suppressNextClickRef.current = false;
      };

  const handlePointerMove = onTableDragStart
    ? undefined
    : (event: React.PointerEvent<HTMLDivElement>) => {
        const start = pointerStartRef.current;
        if (!start || start.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - start.x;
        const deltaY = event.clientY - start.y;

        const threshold =
          start.pointerType === 'touch'
            ? TABLE_TOUCH_CLICK_DRAG_THRESHOLD_PX
            : TABLE_CLICK_DRAG_THRESHOLD_PX;

        if (deltaX * deltaX + deltaY * deltaY > threshold * threshold) {
          markDragLikeGesture();
        }
      };

  const handlePointerEnd = onTableDragStart
    ? undefined
    : (event: React.PointerEvent<HTMLDivElement>) => {
        if (pointerStartRef.current?.pointerId === event.pointerId) {
          pointerStartRef.current = null;
        }
      };

  return (
    <div
      style={{
        ...position,
        ...(onTableDragStart ? { touchAction: 'none' as const } : null),
        ...(hidden
          ? { display: 'none' as const, pointerEvents: 'none' as const }
          : null),
      }}
      data-table-id={table.id}
      data-table-name={table.name}
      data-table-space={table.space}
      data-table-available={available ? '1' : '0'}
      data-table-switch-reservation={switchReservation || ''}
      onClick={onTableDragStart ? undefined : handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      className={classHelper([
        'table-wrapper',
        ((active && !editMode) || isSelected) && 'active',
        sizeHelper(position.height),
      ])}
    >
      <Table
        table={table}
        size={size}
        status={status}
        times={times}
        dropTimes={dropTimes}
        active={(active && !editMode) || isSelected}
        blocked={!!blocked}
        blockPillLabel={blockPillLabel}
        spaceClosedLabel={spaceClosedLabel}
        alert={hasAlert}
        timeline={timeline}
        style={{ position: 'relative', zIndex: 1 }}
        available={available && !isSelected}
        switchReservation={switchReservation}
        touchingX={touchingX}
        touchingY={touchingY}
        sharedEditInfo={sharedEditInfo}
        sharedTablePool={sharedTablePool}
        dragReservationFromTable={!editMode}
        hideGuests={quandooTableView && !editMode}
      />
      {nextResa && (
        <span
          className="quandoo-next-resa-label"
          style={{
            position: 'absolute',
            ...labelUnderTable(
              table,
              size,
              shownTimes,
              spaceClosedLabel ?? blockPillLabel,
              touchingY.length > 0,
            ),
            transform: 'translateX(-50%)',
            maxWidth: '120%',
            marginTop: 2,
            padding: '1px 4px',
            background: '#fff',
            color: '#000',
            fontSize: Math.max(10, Math.min(size, 12)),
            lineHeight: 1.2,
            borderRadius: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {nextResa.guests} {nextResa.guestName}
        </span>
      )}
    </div>
  );
};

export default TableWrapper;
